/**
 * Все строки размещения одного сезона. Сервер отдаёт их страницами не больше 500: первая
 * страница называет общее число, остальные берутся параллельно. Нужны целиком — из них считаются
 * KPI (га по культурам, строки без хозяйства), список хозяйств для фильтра и страницы таблицы.
 */

import type { QueryParams } from "../../api/client";
import { placementApi, type PlacementRow } from "./placementApi";

/** Размер страницы сервера (потолок `MAX_PAGE` на стороне API). */
export const CHUNK = 500;
/** Потолок страниц: 20 000 строк на сезон. Больше — экран честно скажет, что показал не всё. */
export const MAX_CHUNKS = 40;

export interface SeasonRows {
  season: string;
  seasons: string[];
  rows: PlacementRow[];
  /** Всего строк в сезоне по ответу сервера. */
  total: number;
  /** Забрано меньше, чем есть: сезон больше потолка. */
  truncated: boolean;
}

export async function loadSeasonRows(params: QueryParams, season: string): Promise<SeasonRows> {
  let effective = season;
  if (!effective) {
    // Сезон не выбран: узнаём список сезонов дешёвым запросом на одну строку.
    const probe = await placementApi.list({ ...params, limit: 1 });
    effective = probe.seasons[0] ?? "";
    if (!effective) return { season: "", seasons: probe.seasons, rows: [], total: 0, truncated: false };
  }
  const first = await placementApi.list({ ...params, season: effective, limit: CHUNK, offset: 0 });
  const chunks = Math.min(Math.ceil(first.total / CHUNK), MAX_CHUNKS);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, chunks - 1) }, (_, i) =>
      placementApi.list({ ...params, season: effective, limit: CHUNK, offset: (i + 1) * CHUNK }),
    ),
  );
  const rows = [...first.rows, ...rest.flatMap((part) => part.rows)];
  return {
    season: effective,
    seasons: first.seasons,
    rows,
    total: first.total,
    truncated: rows.length < first.total,
  };
}
